import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Image as ImageIcon, Calendar, Award, Plus, Upload, X } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const CompletedWorkPage = () => {
  const [children, setChildren] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [completedWork, setCompletedWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedWork, setSelectedWork] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    subjectId: '',
    title: '',
    description: '',
    pointsAwarded: '',
    pointsPossible: '',
    notes: '',
    dateAssigned: new Date().toISOString().split('T')[0],
    dueDate: new Date().toISOString().split('T')[0],
    completedDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCompletedWork();
  }, [selectedChild, selectedSubject]);

  const loadData = async () => {
    try {
      const [childrenData, subjectsData] = await Promise.all([
        api.getChildren(),
        api.getSubjects()
      ]);
      setChildren(childrenData);
      // Sort subjects alphabetically by name
      const sortedSubjects = subjectsData.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setSubjects(sortedSubjects);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedWork = async () => {
    try {
      const childId = selectedChild === 'all' ? null : selectedChild;
      const subjectId = selectedSubject === 'all' ? null : selectedSubject;
      const data = await api.getCompletedWork(childId, subjectId);
      setCompletedWork(data);
    } catch (error) {
      console.error('Failed to load completed work:', error);
      toast({
        title: 'Error',
        description: 'Failed to load completed work',
        variant: 'destructive'
      });
    }
  };

  const toggleChild = (childId) => {
    if (selectedChildren.includes(childId)) {
      setSelectedChildren(selectedChildren.filter(id => id !== childId));
    } else {
      setSelectedChildren([...selectedChildren, childId]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddAssignment = async () => {
    try {
      if (selectedChildren.length === 0) {
        toast({
          title: 'Error',
          description: 'Please select at least one student',
          variant: 'destructive'
        });
        return;
      }

      // Create assignments for each selected child
      const assignmentData = {
        child_ids: selectedChildren,
        subject_id: formData.subjectId,
        title: formData.title,
        description: formData.description,
        date_assigned: new Date(formData.dateAssigned).toISOString(),
        due_date: new Date(formData.dueDate).toISOString(),
        assignment_type: '4', // Classwork
        points_possible: parseInt(formData.pointsPossible)
      };

      const result = await api.createAssignment(assignmentData);

      // Upload files to S3 and get URLs
      const attachmentsPromises = selectedFiles.map(async (file) => {
        try {
          const uploadResult = await api.uploadFile(file);
          return {
            type: file.type?.startsWith('image/') ? 'image' : 'document',
            name: file.name,
            url: uploadResult.url
          };
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          throw new Error(`Failed to upload ${file.name}`);
        }
      });

      const attachments = await Promise.all(attachmentsPromises);

      // Grade each created assignment immediately
      for (const assignment of result.assignments) {
        // Submit the assignment (simulated)
        await api.submitAssignment(assignment.id, {
          description: 'Completed offline',
          attachments: attachments
        });

        // Grade the assignment
        await api.gradeAssignment(assignment.id, {
          points_awarded: parseFloat(formData.pointsAwarded),
          notes: formData.notes,
          tokens_awarded: 1
        });
      }

      toast({
        title: 'Assignments Added!',
        description: `${formData.title} has been recorded for ${selectedChildren.length} student${selectedChildren.length > 1 ? 's' : ''}.`
      });

      // Reset and reload
      setFormData({
        subjectId: '',
        title: '',
        description: '',
        pointsAwarded: '',
        pointsPossible: '',
        notes: '',
        dateAssigned: new Date().toISOString().split('T')[0],
        dueDate: new Date().toISOString().split('T')[0],
        completedDate: new Date().toISOString().split('T')[0]
      });
      setSelectedChildren([]);
      setSelectedFiles([]);
      setIsAddDialogOpen(false);
      await loadCompletedWork();
    } catch (error) {
      console.error('Failed to add assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to add assignment',
        variant: 'destructive'
      });
    }
  };

  const getSubject = (subjectId) => subjects.find(s => s.id === subjectId);

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'bg-green-100 text-green-700 border-green-300';
    if (percentage >= 80) return 'bg-blue-100 text-blue-700 border-blue-300';
    if (percentage >= 70) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (percentage >= 60) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  const handleWorkClick = (work) => {
    setSelectedWork(work);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Completed Assignments</h1>
              <p className="text-slate-600 text-lg">View graded work and achievements</p>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-6">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Assignment
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-slate-900">
                    Add Completed Assignment
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Student Selection */}
                  <div>
                    <Label>Students (select multiple)</Label>
                    <div className="mt-2 border rounded-lg p-3 bg-slate-50 max-h-40 overflow-y-auto">
                      <div className="space-y-2">
                        {children.map(child => (
                          <div key={child.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`child-${child.id}`}
                              checked={selectedChildren.includes(child.id)}
                              onCheckedChange={() => toggleChild(child.id)}
                            />
                            <Label htmlFor={`child-${child.id}`} className="cursor-pointer text-sm font-normal flex-1">
                              {child.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <Label>Subject</Label>
                    <Select value={formData.subjectId} onValueChange={(value) => setFormData({ ...formData, subjectId: value })}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map(subject => (
                          <SelectItem key={subject.id} value={subject.id}>
                            {subject.icon} {subject.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Title */}
                  <div>
                    <Label>Assignment Title</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Math Quiz Chapter 5"
                      className="mt-2"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of the work completed..."
                      className="mt-2 min-h-[80px]"
                    />
                  </div>

                  {/* Points */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Points Awarded</Label>
                      <Input
                        type="number"
                        value={formData.pointsAwarded}
                        onChange={(e) => setFormData({ ...formData, pointsAwarded: e.target.value })}
                        placeholder="85"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Points Possible</Label>
                      <Input
                        type="number"
                        value={formData.pointsPossible}
                        onChange={(e) => setFormData({ ...formData, pointsPossible: e.target.value })}
                        placeholder="100"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date Assigned</Label>
                      <Input
                        type="date"
                        value={formData.dateAssigned}
                        onChange={(e) => setFormData({ ...formData, dateAssigned: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Completed Date</Label>
                      <Input
                        type="date"
                        value={formData.completedDate}
                        onChange={(e) => setFormData({ ...formData, completedDate: e.target.value, dueDate: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label>Teacher Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Great work! Keep it up..."
                      className="mt-2 min-h-[80px]"
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <Label>Attachments (Optional)</Label>
                    <div className="mt-2">
                      <label className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload files</p>
                          <p className="text-xs text-slate-400 mt-1">Photos, PDFs, documents</p>
                        </div>
                        <input
                          type="file"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          accept="image/*,.pdf,.doc,.docx"
                        />
                      </label>
                    </div>
                    {selectedFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {selectedFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              {file.type?.startsWith('image/') ? (
                                <ImageIcon className="w-4 h-4 text-blue-500" />
                              ) : (
                                <FileText className="w-4 h-4 text-slate-500" />
                              )}
                              <span className="text-sm text-slate-700">{file.name}</span>
                            </div>
                            <button
                              onClick={() => removeFile(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={() => setIsAddDialogOpen(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddAssignment}
                      disabled={!formData.title || !formData.subjectId || selectedChildren.length === 0 || !formData.pointsAwarded || !formData.pointsPossible}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Assignment
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <Label>Filter by Child</Label>
            <Select value={selectedChild} onValueChange={setSelectedChild}>
              <SelectTrigger className="mt-2 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Children</SelectItem>
                {children.map(child => (
                  <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Filter by Subject</Label>
            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger className="mt-2 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjects.map(subject => (
                  <SelectItem key={subject.id} value={subject.id}>
                    {subject.icon} {subject.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Completed Work Grid */}
        {completedWork.length === 0 ? (
          <Card className="p-12 text-center border-0 shadow-lg">
            <FileText className="w-24 h-24 text-slate-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">No Completed Assignments</h2>
            <p className="text-slate-600">Graded assignments will appear here once work is reviewed.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {completedWork.map(work => {
              const subject = getSubject(work.subject_id);
              return (
                <Card
                  key={work.id}
                  className="p-6 border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer bg-white"
                  onClick={() => handleWorkClick(work)}
                >
                  {/* Subject Badge */}
                  {subject && (
                    <div 
                      className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-4"
                      style={{ backgroundColor: `${subject.color}20`, color: subject.color }}
                    >
                      {subject.icon} {subject.name}
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{work.title}</h3>

                  {/* Child Name */}
                  <p className="text-sm text-slate-600 mb-4">{work.child_name}</p>

                  {/* Grade Display */}
                  <div className="flex items-center gap-2 mb-4">
                    <Award className="w-5 h-5 text-amber-500" />
                    <Badge className={`text-sm px-3 py-1 ${getGradeColor(work.percentage)}`}>
                      {work.points_awarded}/{work.points_possible} ({Math.round(work.percentage)}%)
                    </Badge>
                    <Badge variant="outline" className="text-sm">{work.letter_grade}</Badge>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Calendar className="w-4 h-4" />
                    Completed: {new Date(work.completed_date).toLocaleDateString()}
                  </div>

                  {/* Attachments */}
                  {work.attachments && work.attachments.length > 0 && (
                    <div className="mt-3 text-sm text-slate-500">
                      📎 {work.attachments.length} attachment{work.attachments.length > 1 ? 's' : ''}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">
                Assignment Details
              </DialogTitle>
            </DialogHeader>
            {selectedWork && (
              <div className="space-y-6 mt-4">
                {/* Subject */}
                {(() => {
                  const subject = getSubject(selectedWork.subject_id);
                  return subject ? (
                    <div 
                      className="inline-block px-4 py-2 rounded-lg text-base font-medium"
                      style={{ backgroundColor: `${subject.color}20`, color: subject.color }}
                    >
                      {subject.icon} {subject.name}
                    </div>
                  ) : null;
                })()}

                {/* Title & Child */}
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{selectedWork.title}</h3>
                  <p className="text-slate-600 mt-1">Student: {selectedWork.child_name}</p>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-lg font-semibold">Description</Label>
                  <p className="mt-2 text-slate-700">{selectedWork.description}</p>
                </div>

                {/* Grade Info */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-lg font-semibold mb-3 block">Grade</Label>
                  <div className="flex items-center gap-4 flex-wrap">
                    <Badge className={`text-lg px-4 py-2 ${getGradeColor(selectedWork.percentage)}`}>
                      {selectedWork.points_awarded}/{selectedWork.points_possible} points
                    </Badge>
                    <Badge variant="outline" className="text-lg px-4 py-2">
                      {Math.round(selectedWork.percentage)}%
                    </Badge>
                    <Badge variant="outline" className="text-lg px-4 py-2">
                      {selectedWork.letter_grade}
                    </Badge>
                  </div>
                </div>

                {/* Notes */}
                {selectedWork.notes && (
                  <div>
                    <Label className="text-lg font-semibold">Teacher Notes</Label>
                    <p className="mt-2 text-slate-700 bg-blue-50 p-3 rounded-lg">{selectedWork.notes}</p>
                  </div>
                )}

                {/* Tokens */}
                {selectedWork.tokens_awarded > 0 && (
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <span className="text-amber-700 font-medium">
                      🪙 Earned {selectedWork.tokens_awarded} token{selectedWork.tokens_awarded > 1 ? 's' : ''}
                    </span>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label>Assigned</Label>
                    <p className="mt-1 text-slate-600">{new Date(selectedWork.date_assigned).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label>Due</Label>
                    <p className="mt-1 text-slate-600">{new Date(selectedWork.due_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label>Completed</Label>
                    <p className="mt-1 text-slate-600">{new Date(selectedWork.completed_date).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Attachments */}
                {selectedWork.attachments && selectedWork.attachments.length > 0 && (
                  <div>
                    <Label className="text-lg font-semibold mb-3 block">Attachments ({selectedWork.attachments.length})</Label>
                    <div className="space-y-3">
                      {selectedWork.attachments.map((attachment, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
                          {attachment.type === 'image' && attachment.url ? (
                            <div className="space-y-2">
                              <div className="aspect-video bg-slate-100 relative">
                                <img 
                                  src={attachment.url} 
                                  alt={attachment.name}
                                  className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(attachment.url, '_blank')}
                                />
                              </div>
                              <div className="p-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ImageIcon className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm text-slate-700">{attachment.name}</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => window.open(attachment.url, '_blank')}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  View Full Size
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-slate-500" />
                                <span className="text-sm text-slate-700">{attachment.name}</span>
                              </div>
                              {attachment.url && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => window.open(attachment.url, '_blank')}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  Download
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CompletedWorkPage;
